import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, type JobMeta, type RunnerEvent } from "../lib/api";
import { Badge, Button, cn, Icon, LiveDot } from "../components/ui";

export const Route = createFileRoute("/jobs/$id")({
  component: JobLiveViewer,
});

function JobLiveViewer() {
  const { id } = Route.useParams();
  const { t } = useTranslation();
  const [meta, setMeta] = useState<JobMeta | null>(null);
  const [events, setEvents] = useState<RunnerEvent[]>([]);
  const [streamErr, setStreamErr] = useState<string | null>(null);
  const [autoscroll, setAutoscroll] = useState(true);
  const [showSystem, setShowSystem] = useState(true);
  const [showRaw, setShowRaw] = useState(false);
  const logRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setEvents([]);
    setMeta(null);
    setStreamErr(null);
    const es = new EventSource(`/api/research/${encodeURIComponent(id)}/stream`);
    es.addEventListener("meta", (e) => {
      try { setMeta(JSON.parse((e as MessageEvent).data)); } catch {}
    });
    es.addEventListener("log", (e) => {
      try {
        const ev = JSON.parse((e as MessageEvent).data) as RunnerEvent;
        setEvents((prev) => prev.concat(ev));
      } catch {}
    });
    es.addEventListener("end", (e) => {
      try { setMeta(JSON.parse((e as MessageEvent).data)); } catch {}
      es.close();
    });
    es.onerror = () => {
      setStreamErr(t("job.streamError"));
    };
    return () => { es.close(); };
  }, [id, t]);

  useEffect(() => {
    if (!autoscroll) return;
    logRef.current?.scrollTo({ top: logRef.current.scrollHeight });
  }, [events, autoscroll]);

  const filtered = useMemo(
    () => events.filter((e) => showSystem || e.stream !== "system"),
    [events, showSystem],
  );

  const lines = useMemo(() => filtered.map((e) => renderLine(e, showRaw)), [filtered, showRaw]);

  async function onKill() {
    if (!confirm(t("job.killConfirm"))) return;
    await api.killJob(id);
  }

  const reportSlug = useMemo(() => detectSlug(events), [events]);

  return (
    <main className="max-w-[1180px] mx-auto px-6 py-8">
      <header className="flex items-start justify-between gap-6 mb-5 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-[12.5px] text-ink-faint mb-3">
            <Link to="/" className="hover:text-ink inline-flex items-center gap-1">
              <Icon name="arrow-left" size={12} />
              {t("job.breadcrumb")}
            </Link>
            <span>/</span>
            <span className="font-mono truncate">jobs / {id}</span>
          </div>
          <h1 className="font-serif text-[36px] leading-[1.1] font-medium text-ink tracking-tight">
            {t("job.title")} <span className="font-mono text-[17px] text-ink-faint align-baseline">{id}</span>
          </h1>
          {meta && (
            <p className="mt-3 text-[13px] text-ink-dim">
              <span className="font-mono break-all">{meta.target}</span>
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {meta?.status === "running" ? (
            <Badge tone="ok" size="md" className="gap-2">
              <LiveDot />
              {t("job.running")}
            </Badge>
          ) : meta ? (
            <StatusBadge status={meta.status} />
          ) : null}
          {meta?.status === "running" && (
            <Button variant="danger-ghost" size="sm" icon="stop" onClick={onKill}>
              {t("job.stop")}
            </Button>
          )}
          {reportSlug && (
            <Link
              to="/sessions/$slug"
              params={{ slug: reportSlug }}
              className="h-8 px-3 text-[13px] gap-1.5 rounded-md inline-flex items-center justify-center bg-surface border border-border text-ink hover:bg-surface-2 hover:border-border-strong"
            >
              {t("job.openReport")}
              <Icon name="arrow-right" size={13} />
            </Link>
          )}
        </div>
      </header>

      {streamErr && (
        <div className="mb-4 rounded-lg border border-danger/30 bg-danger-soft px-4 py-3 text-sm text-danger">
          {streamErr}
        </div>
      )}

      <section className="rounded-lg border border-border bg-surface overflow-hidden">
        <div className="flex items-center justify-between gap-3 border-b border-border bg-surface-2 px-4 py-2.5 text-xs flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex items-center gap-1.5 shrink-0">
              <span className="w-2.5 h-2.5 rounded-full bg-danger" />
              <span className="w-2.5 h-2.5 rounded-full bg-warn" />
              <span className="w-2.5 h-2.5 rounded-full bg-ok" />
            </div>
            <span className="font-mono text-ink-dim truncate">research.log · {t("job.lines", { count: filtered.length })}</span>
          </div>
          <div className="flex items-center gap-3 text-ink-faint flex-wrap">
            <button
              type="button"
              className="inline-flex items-center gap-1 hover:text-ink"
              onClick={() => navigator.clipboard?.writeText(lines.map((l) => `${l.ts} ${l.stream} ${l.text}`).join("\n"))}
            >
              <Icon name="copy" size={12} />
              {t("job.copy")}
            </button>
            <Toggle checked={autoscroll} onChange={setAutoscroll} label={t("job.autoscroll")} />
            <Toggle checked={showSystem} onChange={setShowSystem} label={t("job.system")} />
            <Toggle checked={showRaw} onChange={setShowRaw} label={t("job.rawJson")} />
          </div>
        </div>
        <div ref={logRef} className="terminal px-5 py-4 max-h-[480px] min-h-[360px] overflow-y-auto">
          {lines.length === 0 && <div className="text-ink-faint">{t("job.waiting")}</div>}
          {lines.map((l, i) => (
            <div key={i} className={cn("grid grid-cols-[4.5rem_4.5rem_1fr] gap-3 py-0.5", l.cls)}>
              <span className="ts font-mono">{l.ts}</span>
              <span className={cn("font-mono", l.tagClass)}>{l.stream}</span>
              <span className="whitespace-pre-wrap break-words">{l.text}</span>
            </div>
          ))}
          {meta?.status === "running" && (
            <div className="grid grid-cols-[4.5rem_4.5rem_1fr] gap-3 py-0.5 text-ink-faint">
              <span className="ts font-mono">--:--:--</span>
              <span className="font-mono">system</span>
              <span className="inline-flex items-center gap-2">
                <Icon name="spinner" size={13} />
                {t("job.thinking")}
              </span>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}

function Toggle({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <label className="inline-flex items-center gap-1.5">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span>{label}</span>
    </label>
  );
}

function StatusBadge({ status }: { status: JobMeta["status"] }) {
  const { t } = useTranslation();
  const tone = status === "done" ? "ok" : status === "error" ? "danger" : "neutral";
  const label =
    status === "done" ? t("job.statusDone") :
    status === "error" ? t("job.statusError") :
    status === "killed" ? t("job.statusKilled") :
    t("job.statusRunning");
  return <Badge tone={tone} size="md">{label}</Badge>;
}

interface RenderedLine { cls: string; tagClass: string; ts: string; stream: string; text: string }

function renderLine(ev: RunnerEvent, raw: boolean): RenderedLine {
  const d = new Date(ev.ts);
  const ts = Number.isNaN(d.getTime()) ? "--:--:--" : d.toISOString().slice(11, 19);
  const tagClass =
    ev.stream === "stderr" ? "tag-err" :
    ev.stream === "system" ? "ts" : "";
  const cls =
    ev.stream === "stderr" ? "text-danger" :
    ev.stream === "system" ? "text-ink-faint" : "text-ink-dim";
  if (raw || ev.stream !== "stdout") {
    return { cls, tagClass, ts, stream: ev.stream, text: ev.text };
  }
  // For stdout, try to parse claude stream-json events into readable lines.
  const parsed = tryParseClaudeEvent(ev.text);
  return { cls, tagClass, ts, stream: ev.stream, text: parsed ?? ev.text };
}

interface ClaudeEventLike {
  type?: string;
  subtype?: string;
  message?: {
    role?: string;
    content?: string | Array<{ type?: string; text?: string; name?: string; input?: unknown; tool_use_id?: string }>;
  };
  delta?: { type?: string; text?: string };
}

function tryParseClaudeEvent(line: string): string | null {
  if (!line.startsWith("{")) return null;
  let obj: ClaudeEventLike;
  try { obj = JSON.parse(line) as ClaudeEventLike; } catch { return null; }
  if (!obj || typeof obj !== "object") return null;
  const t = obj.type;
  if (t === "system") {
    const sub = obj.subtype ?? "system";
    return `[sys/${sub}]`;
  }
  if (t === "assistant" && obj.message) {
    const c = obj.message.content;
    if (typeof c === "string") return `[assistant] ${c}`;
    if (Array.isArray(c)) {
      const parts = c.map((b) => {
        if (b.type === "text") return b.text ?? "";
        if (b.type === "tool_use") return `[tool] ${b.name ?? "?"}(${truncate(JSON.stringify(b.input), 120)})`;
        if (b.type === "thinking") return `[thinking] ${truncate(String((b as { thinking?: string }).thinking ?? ""), 200)}`;
        return `[${b.type ?? "?"}]`;
      }).filter(Boolean);
      return parts.join("\n");
    }
  }
  if (t === "user" && obj.message) {
    const c = obj.message.content;
    if (typeof c === "string") return `[user] ${c}`;
    if (Array.isArray(c)) {
      const parts = c.map((b) => {
        if (b.type === "text") return `[user] ${b.text ?? ""}`;
        if (b.type === "tool_result") return `[tool_result] ${truncate(JSON.stringify((b as { content?: unknown }).content), 200)}`;
        return `[${b.type ?? "?"}]`;
      }).filter(Boolean);
      return parts.join("\n");
    }
  }
  if (t === "stream_event") {
    const d = obj.delta;
    if (d?.type === "text_delta") return d.text ?? "";
    return null;
  }
  if (t === "result") {
    const r = obj as ClaudeEventLike & { result?: string };
    return `[result] ${truncate(r.result ?? "", 400)}`;
  }
  return null;
}

function truncate(s: string, n: number) {
  return s.length > n ? s.slice(0, n) + "..." : s;
}

function detectSlug(events: RunnerEvent[]): string | null {
  // Heuristic: look for "research/<date>-<slug>/README.md" mentions in any stdout/stderr line.
  for (let i = events.length - 1; i >= 0; i--) {
    const m = events[i]!.text.match(/research\/(\d{4}-\d{2}-\d{2}-[a-z0-9][a-z0-9-]*)\/README\.md/i);
    if (m && m[1]) return m[1];
  }
  return null;
}
